import { generateUniqueID } from '../../services/global-functions';
import { AiService } from '../../services/ai.service';
import EditorJS, { API, BlockMutationEvent } from '@editorjs/editorjs';
import * as cheerio from 'cheerio';
import Header from '@editorjs/header';
import List from '@editorjs/list';
import Table from '@editorjs/table';
import DragDrop from 'editorjs-drag-drop';
import edjsParser from 'editorjs-parser';
import { BaseBlockEditor, AIStatusType } from './editor-base';

export default class LearmoEditorJS extends BaseBlockEditor {
  public debouncing = false;
  public preventEditorSave = false;
  public muteChangeEmit = false;

  public parser: edjsParser = new edjsParser();
  public editor!: EditorJS;
  public editorID!: string;

  constructor(
    initialConfiguration: {
      content?: string;
      editorID?: string;
      promptFunction?: (...args: any[]) => string;
    },
    private ai?: AiService,
  ) {
    super(initialConfiguration.promptFunction);
    Object.assign(this, initialConfiguration);
    this.editorID = this.editorID || generateUniqueID();
    this.initializeEditor(this.editorID);
  }

  override getEditorID(): string {
    return this.editorID;
  }

  override async renderContent(value: string): Promise<void> {
    await this.setHTMLContent(value);
  }

  override initializeEditor(editorID = this.editorID): void {
    this.isInitialized = true;
    console.log('Initializing EditorID', editorID);

    this.editor = new EditorJS({
      holder: editorID,
      hideToolbar: false,
      tools: {
        header: { class: Header, inlineToolbar: ['bold', 'link'] },
        list: { class: List, inlineToolbar: ['link', 'bold'] },
        table: Table,
      },
      onChange: (
        api: API,
        event: BlockMutationEvent | BlockMutationEvent[] | any,
      ) => {
        const isRealEvent =
          (event as CustomEvent)?.type === 'block-changed' ||
          (event as CustomEvent)?.type === 'block-removed';

        const isRealEventArray =
          Array.isArray(event) &&
          event.some(
            (e) => e.type === 'block-changed' || e.type === 'block-removed',
          );

        if ((isRealEvent || isRealEventArray) && !this.preventEditorSave) {
          this.saveEditorCurrentValue();
        }
      },
      onReady: () => {
        new DragDrop(this.editor);
        if (this.content) {
          this.setHTMLContent(this.content, true);
        }
        setTimeout(() => {
          this.isEditorReady = true;
          this.emitReady();
        }, 500);
      },
    });
  }

  override clearEditor(): void {
    this.preventEditorSave = true;
    this.editor.blocks.clear();
    this.content = '';
    this.resetAiState();
    this.preventEditorSave = false;
  }

  override async setHTMLContent(
    value: string,
    muteChangeEmit = false,
  ): Promise<void> {
    this.muteChangeEmit = muteChangeEmit;
    this.content = value;
    await this.editor.blocks.renderFromHTML(value);
    if (!this.muteChangeEmit) {
      this.emitChange(value);
    } else {
      this.muteChangeEmit = false;
    }
  }

  override saveEditorCurrentValue(): void {
    this.editor
      .save()
      .then((jsonData) => {
        const htmlContent = this.parser.parse(jsonData);
        this.content = htmlContent;
        if (!this.muteChangeEmit) {
          this.emitChange(htmlContent);
        } else {
          this.muteChangeEmit = false;
        }
      })
      .catch((err) => console.log('Saving failed: ', err));
  }

  override async startGenerating(
    promptFunction?: (...args: any[]) => string,
  ): Promise<boolean> {
    const prompt = promptFunction?.() || this.promptFunction?.();
    if (!prompt || !this.ai) return false;

    this.setAiStatus('loading');
    this.startTimer();
    this.clearEditor();

    return new Promise((resolve) => {
      this.ai!.getCompletion(prompt, { stream: true }).subscribe({
        next: (response: string) => {
          this.aiGeneratedContent += response;
          this.scrollToEditorEnd();
        },
        error: () => {
          this.setAiStatus('error');
          this.clearTimer();
          resolve(false);
        },
        complete: () => {
          this.stopTimer();
          this.setAiStatus('finalizing');

          this.aiGeneratedContent = this.optimizeHTMLContent(
            this.aiGeneratedContent,
          );

          this.setHTMLContent(this.aiGeneratedContent).finally(() => {
            setTimeout(() => {
              this.saveEditorCurrentValue();
              this.setAiStatus('done');
              this.resetAiState();
              resolve(true);
            }, 1500);
          });
        },
      });
    });
  }
}
